
import { defineComponent, ref, reactive, toRefs, computed, watch, nextTick } from 'vue'
import { DeleteOutlined } from '@ant-design/icons-vue'
import { askApi } from '@/api/index'
import quantApi from '@/api/quantApi'

export default defineComponent({
  components: { DeleteOutlined },
  props: {
    token:   { type: String, default: '' },
    source:  { type: String, default: '' },
    navObj:  { type: Object, default: () => ({}) },
    theme:   { type: String, default: 'light' },
    tags:    { type: Object, default: () => ({}) }
  },
  setup(props: any, ctx: any) {
    const pageData: any = reactive({
      value: '',
      placeholder: '搜索股票',
      options: [],
      historyData: JSON.parse(sessionStorage.getItem('askingHistory') || '[]'),
      historyOption: [],
      searchstr: '',
      tags: {},
      activeLi: '',
      activeindex: -1,
      showDropdown: false,
      isComposing: false
    })

    const askingselect = ref()

    // ── 工具 ──────────────────────────────────────────────
    function debounce(fn: Function, wait: number) {
      let timer: any
      return function (this: any, ...args: any[]) {
        clearTimeout(timer)
        timer = setTimeout(() => fn.apply(this, args), wait)
      }
    }

    function isEmpty(obj: any) {
      return Object.keys(obj).length === 0
    }

    function marketLabel(code: string) {
      const map: Record<string, string> = { sh: '沪A', sz: '深A', bj: '京A' }
      return map[code] || ''
    }

    // ── 计算属性 ──────────────────────────────────────────
    const selectOption = computed(() => {
      const { searchstr, historyOption, options } = pageData
      return !searchstr.length && historyOption.length ? historyOption : options
    })

    // ── 历史记录 ──────────────────────────────────────────
    function saveHistory() {
      sessionStorage.setItem('askingHistory', JSON.stringify(pageData.historyData))
    }

    function addHistory(obj: any) {
      pageData.historyData.push(obj)
      saveHistory()
    }

    function clearHistory() {
      pageData.historyData = []
      pageData.historyOption = []
      pageData.showDropdown = false
      sessionStorage.removeItem('askingHistory')
    }

    // ── 搜索 ──────────────────────────────────────────────
    function searchValue(val: string) {
      if (!pageData.value) return
      pageData.searchstr = pageData.value
      quantApi.searchStock({
        data: { keyword: pageData.value },
        headers: { token: props.token }
      }).then((res: any) => {
        nextTick(() => {
          const list = res?.data?.security_all || []
          pageData.options = list.map((item: any) => ({
            Id: item.symbol,
            Code: item.symbol,
            Name: item.presentName || item.name,
            MarketCode: item.market
          }))
          if (pageData.options.length) {
            pageData.activeLi = pageData.options[0].Id
            pageData.activeindex = 0
          }
        })
      })
    }

    const debouncedSearch = debounce(searchValue, 300)

    // ── 选中 ──────────────────────────────────────────────
    function emit(obj: any) {
      ctx.emit('checkStock', obj)
      addHistory(obj)
    }

    function handleChange(e: any, value: any) {
      e.stopPropagation()
      pageData.value = ''
      pageData.searchstr = ''
      emit(value)
      closeDropdown()
      return false
    }

    function closeDropdown() {
      pageData.showDropdown = false
      saveHistory()
      pageData.searchstr = ''
      nextTick(() => askingselect.value?.blur())
    }

    function searchAndClose() {
      if (!isEmpty(props.tags)) return
      closeDropdown()
    }

    // ── 输入框事件 ────────────────────────────────────────
    function handleCompositionStart() { pageData.isComposing = true }
    function handleCompositionEnd() {
      pageData.isComposing = false
      if (pageData.value) debouncedSearch(pageData.value)
    }

    function handleInputChange() {
      if (pageData.isComposing) return
      pageData.searchstr = pageData.value
      pageData.showDropdown = true
      debouncedSearch(pageData.value)
    }

    function handlefocusInput() {
      pageData.activeLi = ''
      pageData.activeindex = -1
      askingselect.value?.focus()
    }

    function focusSelect() {
      pageData.showDropdown = true
      pageData.activeLi = ''
      pageData.searchstr = ''
      pageData.activeindex = -1
      window.addEventListener('keydown', handlekeyFn, true)

      let history = [...(JSON.parse(sessionStorage.getItem('askingHistory') || '[]'))].reverse()
      const map = new Map(history.map((i: any) => [i.Id, i]))
      pageData.historyOption = [...map.values()].slice(0, 10)
      pageData.options = []

      if (!pageData.historyOption.length) searchValue(pageData.value)
    }

    function blurSelect() {
      window.removeEventListener('keydown', handlekeyFn)
      setTimeout(() => {
        pageData.showDropdown = false
        if (!pageData.activeLi) enterSelect()
      }, 400)
    }

    async function enterSelect() {
      if (!pageData.activeLi && !pageData.value) return
      if (!pageData.activeLi) return

      const obj = selectOption.value.find((i: any) => i.Id === pageData.activeLi) || null

      if (obj) {
        emit(obj)
        closeDropdown()
      }
      searchAndClose()
    }

    function inputKeyup(e: any) {
      if (e.code === 'Enter') enterSelect()
    }

    function handlekeyFn(e: any) {
      if (e.code !== 'ArrowDown' && e.code !== 'ArrowUp') return
      const len = selectOption.value.length
      if (!len) return

      if (e.code === 'ArrowDown') {
        pageData.activeindex = Math.min(pageData.activeindex + 1, len - 1)
      } else {
        pageData.activeindex = Math.max(pageData.activeindex - 1, 0)
      }
      pageData.activeLi = selectOption.value[pageData.activeindex].Id

      const dom: any = document.querySelector('.searchlist')
      if (dom) dom.scrollTop = pageData.activeindex < 10 ? 0 : pageData.activeindex * 32
    }

    // ── 监听父组件 tags 变化回显 ──────────────────────────
    watch(
      () => props.tags,
      () => {
        pageData.value = props.tags?.Code ? `${props.tags.Name}(${props.tags.Code})` : ''
      },
      { deep: true }
    )

    return {
      ...toRefs(pageData),
      selectOption,
      askingselect,
      marketLabel,
      handleChange,
      handlefocusInput,
      focusSelect,
      blurSelect,
      inputKeyup,
      handlekeyFn,
      handleInputChange,
      handleCompositionStart,
      handleCompositionEnd,
      clearHistory,
      enterSelect,
      isEmpty
    }
  }
})
